import React, { useState, useEffect } from 'react';
import { Provider as JotaiProvider } from 'jotai';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { ClientConfigLoader } from '../components/ClientConfigLoader';
import { ClientConfigProvider } from '../hooks/useClientConfig';
import { InfoDocParamsProvider } from '../hooks/useInfoDocParams';
import { ConfigConfigError, ConfigConfigLoading } from './ConfigConfig';
import { FeatureCheck } from './FeatureCheck';
import { createRouter } from './Router';
import { ScreenSizeProvider, useScreenSize } from '../hooks/useScreenSize';
import buildConfig from '../../../build.config';

const queryClient = new QueryClient();

function WidgetToggle({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  // 直接在 handler 內調整外部 iframe 大小
  const handleToggle = () => {
    setOpen((v) => {
      const next = !v;
      // 只在 iframe 內執行
      const iframe = window.frameElement as HTMLIFrameElement | null;
      if (iframe) {
        const loadchatDiv = window.parent.document.getElementById('loadchat');
        iframe.style.top = '';
        iframe.style.bottom = '0px';

        if (loadchatDiv) {
          if (next) {
            loadchatDiv.style.width = '80%';
            loadchatDiv.style.height = '80%';
          } else {
            loadchatDiv.style.width = '100px';
            loadchatDiv.style.height = '100px';
          }
          iframe.style.width = '100%';
          iframe.style.height = '100%';
        } else {
          // original logic
          if (next) {
            iframe.style.width = '100%';
            iframe.style.height = '100%';
          } else {
            iframe.style.width = '100px';
            iframe.style.height = '100px';
          }
        }
      }
      return next;
    });
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          right: 24, // 改為靠右
          bottom: 0,
          zIndex: 12,
        }}
      >
        <button
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'var(--curve-notice-bg, #2DD3F4)',
            border: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
          onClick={handleToggle}
          aria-label={open ? 'Close Chat Widget' : 'Open Chat Widget'}
        >
          {open ? (
            // X icon SVG
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="8" y1="8" x2="24" y2="24" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
              <line x1="24" y1="8" x2="8" y2="24" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
            </svg>
          ) : (
            <img src={`${buildConfig.base}public/res/img/InfoDocLogo.png`} alt="Chat" style={{ width: 32, height: 32 }} />
          )}
        </button>
      </div>
      {open && (
        <div
          style={{
            position: 'fixed',
            bottom: 96,
            width: '100%',
            height: '80%',
            background: 'transparent', // 背景透明
            borderRadius: 16,
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            zIndex: 9,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </div>
      )}
    </>
  );
}

function App() {
  const screenSize = useScreenSize();
  const [hasRoomSelected, setHasRoomSelected] = useState(false);
  const [roomClosed, setRoomClosed] = useState(false);
  const [infodocparams, setInfodocparams] = useState();

  useEffect(() => {
    if (buildConfig.enableWidgetMode) {
      let infodoctoken: string | undefined = window.infodoctoken || undefined;
      // console.log('App: infodoctoken', infodoctoken);
      if (infodoctoken !== undefined) localStorage.setItem('infodoctoken', infodoctoken);
      else infodoctoken = localStorage.getItem('infodoctoken') || undefined;
      // console.log('App: infodoctoken', infodoctoken);
      /*
          const urlParams : URLSearchParams = new URLSearchParams(window.location.search);
          const paramstoken = urlParams.get('infodoctoken');
          console.log('App: paramstoken', paramstoken);
          if (!infodoctoken) infodoctoken = paramstoken || undefined;
      */
      const params = {
        infodoctoken: infodoctoken || undefined,
      }
      setInfodocparams(params);
    }
  }, []);

  useEffect(() => {
    if (buildConfig.enableWidgetMode && buildConfig.NoIconToggle) {
      const iframe = window.frameElement as HTMLIFrameElement | null;
      if (iframe) {
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        const loadchatDiv = window.parent.document.getElementById('loadchat');
        if (loadchatDiv) {
          loadchatDiv.style.width = '80%';
          loadchatDiv.style.height = '80%';
        }
      }
    }
  }, []);

  const appContent = (
    <ScreenSizeProvider value={screenSize}>
      <FeatureCheck>
        <ClientConfigLoader
          fallback={() => <ConfigConfigLoading />}
          error={(err, retry, ignore) => (
            <ConfigConfigError error={err} retry={retry} ignore={ignore} />
          )}
        >
          {(clientConfig) => (
            <ClientConfigProvider value={clientConfig}>
              <QueryClientProvider client={queryClient}>
                <JotaiProvider>
                  <InfoDocParamsProvider value={infodocparams}>
                    <RouterProvider router={createRouter(clientConfig, screenSize, hasRoomSelected, setHasRoomSelected, roomClosed, setRoomClosed)} />
                  </InfoDocParamsProvider>
                </JotaiProvider>
              </QueryClientProvider>
            </ClientConfigProvider>
          )}
        </ClientConfigLoader>
      </FeatureCheck>
    </ScreenSizeProvider>
  );

  if (buildConfig.enableWidgetMode) {
    if (buildConfig.NoIconToggle) {
      return appContent;
    }
    return (
      <WidgetToggle>
        {appContent}
      </WidgetToggle>
    );
  }

  return appContent;
}

export default App;
